import { HolderOutlined } from "@ant-design/icons"
import type { DragEndEvent } from "@dnd-kit/core"
import { DndContext } from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Button, Card, Divider, Empty, Flex, Form, Tag, Tooltip, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { memo, useCallback, useEffect, useState } from "react"

import { SearchBar } from "../../../../../components/Input/SearchBar.tsx"
import { cn } from "../../../../../utils/cn.ts"
import {
  type ProjectTypeAddPhaseFormData,
  type ProjectTypeTemplatePhase
} from "../../../types/project-type.types.ts"
import { type ProjectTypeFormData } from "../ProjectType.tsx"
import { ProjectTypeMilestonesFooter } from "./ProjectTypeMilestonesFooter.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

const { Title, Text } = Typography

interface ProjectTypeMilestonesProps {
  form: FormInstance<ProjectTypeFormData>
  selectedPhaseId?: string | null
  phases: ProjectTypeTemplatePhase[]
  disabled?: boolean
  createAllowed?: boolean
  onSelectPhase: (phase: ProjectTypeTemplatePhase) => void
  onAddPhase: (data: ProjectTypeAddPhaseFormData) => void
}

export function ProjectTypeMilestones({
  form,
  phases: initialPhases,
  selectedPhaseId,
  createAllowed,
  disabled,
  onSelectPhase,
  onAddPhase
}: ProjectTypeMilestonesProps) {
  const count = initialPhases?.length || 0

  const [phases, setMutatedPhases] =
    useState<ProjectTypeTemplatePhase[]>(initialPhases)
  const [searchValue, setSearchValue] = useState<string | null>("")

  const onSearch = useCallback(
    (value: string | null) => {
      setSearchValue(value)
      if (!value) return setMutatedPhases(initialPhases)

      const filtered = initialPhases?.filter((p) =>
        p?.name?.toLowerCase()?.includes(value?.toLowerCase())
      )
      setMutatedPhases(filtered)
    },
    [initialPhases]
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const currentPhases = form.getFieldValue(["projectType", "phases"]) || []
    const activeIndex = currentPhases.findIndex(
      (p: ProjectTypeTemplatePhase) => p.id === active.id
    )
    const overIndex = currentPhases.findIndex(
      (p: ProjectTypeTemplatePhase) => p.id === over.id
    )

    if (activeIndex !== -1 && overIndex !== -1) {
      const newPhases = arrayMove(currentPhases, activeIndex, overIndex).map(
        (phase: any, index: number) => ({
          ...phase,
          order: index
        })
      )

      // Update form values
      form.setFieldsValue({
        projectType: {
          phases: newPhases
        }
      })

      setMutatedPhases(newPhases)
    }
  }

  const isSearching = searchValue && searchValue?.length > 0

  useEffect(() => {
    const initPhases = initialPhases || []

    setMutatedPhases(initPhases)
    form.setFieldValue(["projectType", "phases"], initPhases)
  }, [initialPhases])

  return (
    <Card classNames={{ body: "p-0" }}>
      <Flex vertical gap={"middle"} className={"py-2"}>
        <Flex vertical gap={"small"} className={"px-2"}>
          <Flex align={"center"} justify={"space-between"}>
            <Title level={5}>Milestones</Title>
            <Tag className={"m-0"}>
              {count} milestone{count === 1 ? "" : "s"}
            </Tag>
          </Flex>
          <SearchBar onSearch={onSearch} placeholder={"Search milestones..."} />
        </Flex>

        <Divider className={"m-0"} />

        <div className={"px-2 max-h-[calc(100vh-640px)] overflow-y-auto"}>
          <Form.List name={["projectType", "phases"]}>
            {(fields) => {
              if (!fields?.length || !phases?.length) {
                return (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={"No milestones found"}
                  />
                )
              }

              return (
                <DndContext
                  modifiers={[restrictToVerticalAxis]}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={phases.map((p) => p?.id)}
                    strategy={verticalListSortingStrategy}
                    disabled={isSearching || phases.length < 2}
                  >
                    <Flex vertical gap={"small"}>
                      {phases?.map((p, index) => (
                        <React.Fragment key={p?.id}>
                          <SortableItem
                            id={p.id}
                            phase={p}
                            fieldIndex={index}
                            isActive={p?.id === selectedPhaseId}
                            disabled={disabled}
                            onSelectPhase={onSelectPhase}
                            isDraggable={!isSearching}
                          />

                          {/* Hidden form fields to maintain form state */}
                          <Form.Item name={[index, "id"]} hidden>
                            <TextInput disableAutoCapitalize />
                          </Form.Item>
                          <Form.Item name={[index, "name"]} hidden>
                            <TextInput disableAutoCapitalize />
                          </Form.Item>
                          <Form.Item name={[index, "description"]} hidden>
                            <TextInput disableAutoCapitalize />
                          </Form.Item>
                          <Form.Item name={[index, "order"]} hidden>
                            <TextInput disableAutoCapitalize />
                          </Form.Item>
                          <Form.Item
                            name={[index, "default_duration_days"]}
                            hidden
                          >
                            <TextInput disableAutoCapitalize />
                          </Form.Item>
                          <Form.Item name={[index, "default_budget"]} hidden>
                            <TextInput disableAutoCapitalize />
                          </Form.Item>
                        </React.Fragment>
                      ))}
                    </Flex>
                  </SortableContext>
                </DndContext>
              )
            }}
          </Form.List>
        </div>

        {createAllowed && (
          <>
            <Divider className={"m-0"} />

            <ProjectTypeMilestonesFooter
              saving={disabled}
              onAddPhase={onAddPhase}
            />
          </>
        )}
      </Flex>
    </Card>
  )
}

interface SortableItemProps {
  id: string
  phase: ProjectTypeTemplatePhase
  fieldIndex: number
  disabled?: boolean
  isActive: boolean
  isDraggable: boolean
  onSelectPhase: (phase: ProjectTypeTemplatePhase) => void
}

const SortableItem = memo(
  ({
    id,
    phase,
    fieldIndex,
    isActive,
    disabled,
    onSelectPhase,
    isDraggable
  }: SortableItemProps) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({ id, disabled: !isDraggable })

    const duration = phase?.default_duration_days || 0

    const onSelectHandler = () => {
      onSelectPhase(phase)
    }

    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      ...(isDragging
        ? {
            position: "relative",
            zIndex: 9999,
            opacity: 0.5
          }
        : {})
    }

    return (
      <div ref={setNodeRef} style={style}>
        <Tooltip placement="right" title={phase?.description}>
          <Flex
            gap={"small"}
            align={"center"}
            className={cn(
              "border border-border p-2 duration-300 cursor-pointer rounded-card transition-all",
              "hover:border-primary/80",
              {
                "border-primary bg-primary/5": isActive,
                "pointer-events-none": disabled
              }
            )}
          >
            {isDraggable && (
              <Button
                type="text"
                size="small"
                icon={<HolderOutlined className={"text-lg"} />}
                style={{ cursor: "move" }}
                {...attributes}
                {...listeners}
              />
            )}
            <button
              className={
                "overflow-hidden flex flex-1 cursor-pointer text-start justify-between items-start gap-3 disabled:opacity-50"
              }
              type={"button"}
              disabled={disabled}
              onClick={onSelectHandler}
            >
              <Flex vertical className={"overflow-hidden"}>
                <Text strong ellipsis={{ tooltip: phase?.name }}>
                  {phase?.name || "No phase name"}
                </Text>
                <Text type={"secondary"}>
                  {duration} Day{duration === 1 ? "" : "s"}
                </Text>
              </Flex>
              {/*{true ? (*/}
              {/*  <Tag className={"m-0"} color={"green"}>*/}
              {/*    Active*/}
              {/*  </Tag>*/}
              {/*) : (*/}
              {/*  <Tag className={"m-0"} color={"red"}>*/}
              {/*    Inactive*/}
              {/*  </Tag>*/}
              {/*)}*/}
            </button>
          </Flex>
        </Tooltip>
      </div>
    )
  }
)

SortableItem.displayName = "ProjectTypeMilestoneItem"
